import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { PlaylistAddCheck } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

interface CaseWorkflowPanelProps {
  caseId: string;
  caseType?: string | null;
}

const CaseWorkflowPanel: React.FC<CaseWorkflowPanelProps> = ({ caseId, caseType }) => {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: workflows = [], isLoading, error } = useQuery({
    queryKey: ['workflows', caseType || 'all'],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (caseType) params.set('caseType', caseType);
      const res = await fetch(`/api/workflows?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to load workflows'));
      const json = await res.json();
      return json.data?.workflows || json.data || [];
    },
  });

  const selected = workflows.find((w: any) => w.id === selectedId) || null;

  const applyMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      const res = await fetch(`/api/workflows/${workflowId}/apply/${caseId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to apply workflow'));
      return res.json();
    },
    onSuccess: (json) => {
      const count = json.data?.tasksCreated?.length || json.data?.createdTasks?.length || 0;
      toast.success(count ? `Created ${count} task(s) from workflow` : 'Workflow applied');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setPreviewOpen(false);
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Failed to apply workflow')),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load workflows.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Matter checklists
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Apply a firm workflow to generate starter tasks for this case.
      </Typography>

      {workflows.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PlaylistAddCheck sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">No workflows available for this matter type.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            label="Workflow"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            sx={{ minWidth: 280 }}
          >
            <MenuItem value="">Select a workflow…</MenuItem>
            {workflows.map((w: any) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
                {w.isSystem ? ' (system)' : ''}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            disabled={!selected}
            onClick={() => setPreviewOpen(true)}
          >
            Preview checklist
          </Button>
          <Button
            variant="contained"
            disabled={!selected || applyMutation.isPending}
            onClick={() => selected && applyMutation.mutate(selected.id)}
          >
            Apply workflow
          </Button>
        </Box>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected?.name || 'Checklist'}</DialogTitle>
        <DialogContent>
          {selected?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selected.description}
            </Typography>
          )}
          <List dense>
            {(selected?.tasks || []).map((t: any, idx: number) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={t.title}
                  secondary={[
                    t.priority ? `Priority: ${t.priority}` : null,
                    t.daysFromStart != null ? `Due +${t.daysFromStart} days from start` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                {t.priority && <Chip size="small" label={t.priority} sx={{ ml: 1 }} />}
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={!selected || applyMutation.isPending}
            onClick={() => selected && applyMutation.mutate(selected.id)}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseWorkflowPanel;
